// Crash and error reports to snapcook_client_errors, so production failures are seen.
//
// Privacy: a report is the error's message and stack, the screen's path and the platform. Before
// anything leaves the device, emails, tokens, long ids, query strings and URL fragments are removed,
// and nothing typed, scanned or in the profile is ever included. Off in Profile → Privacy and data.
// The same error is sent at most once per session and at most 10 per session; the server also caps
// each person at 30 an hour.

import { Capacitor } from '@capacitor/core';
import { useStore } from '../state/store';
import { config, hasBackend } from './config';
import { getSupabase } from './supabase';

export type ErrorKind = 'error' | 'rejection' | 'render';

const MAX_PER_SESSION = 10;
const sent = new Set<string>();

/** Noise that says nothing about our code. */
const IGNORE = [/ResizeObserver loop/i, /^Script error\.?$/i, /^AbortError/i, /aborted/i, /Load failed$/i, /Failed to fetch$/i, /NetworkError/i];

/** Removes anything that could identify a person or carry a secret. */
export function scrub(text: string): string {
  return text
    .replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, '[email]')
    .replace(/eyJ[\w-]+\.[\w-]+\.[\w-]*/g, '[token]')
    .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, '[id]')
    .replace(/\b[A-Za-z0-9_-]{32,}\b/g, '[token]')
    .replace(/(https?:\/\/[^\s?#)]+)[?#][^\s)]*/g, '$1');
}

/** Same error, same fingerprint: digits and line numbers don't split one bug into many. */
export function fingerprint(kind: ErrorKind, message: string, stack: string): string {
  const frame = stack.split('\n').map((l) => l.trim()).find((l) => /^at |@/.test(l)) ?? '';
  const key = `${kind}|${message.replace(/\d+/g, '#')}|${frame.replace(/:\d+(:\d+)?/g, '').replace(/\?[^)\s]*/, '')}`;
  let h1 = 0x811c9dc5, h2 = 0x01000193;
  for (let i = 0; i < key.length; i++) {
    h1 = Math.imul(h1 ^ key.charCodeAt(i), 16777619);
    h2 = Math.imul(h2 ^ key.charCodeAt(i), 2246822519);
  }
  return (h1 >>> 0).toString(16).padStart(8, '0') + (h2 >>> 0).toString(16).padStart(8, '0');
}

function platform(): 'web' | 'pwa' | 'ios' {
  if (Capacitor.isNativePlatform()) return 'ios';
  try { return window.matchMedia('(display-mode: standalone)').matches ? 'pwa' : 'web'; } catch { return 'web'; }
}

function describe(error: unknown): { message: string; stack: string } {
  // DOMException isn't an Error everywhere, so anything with a name and message counts.
  const e = error as { name?: unknown; message?: unknown; stack?: unknown } | null;
  if (e && typeof e === 'object' && typeof e.message === 'string') {
    return { message: `${typeof e.name === 'string' ? e.name : 'Error'}: ${e.message}`, stack: typeof e.stack === 'string' ? e.stack : '' };
  }
  if (typeof error === 'string') return { message: error, stack: '' };
  try { return { message: JSON.stringify(error) ?? String(error), stack: '' }; } catch { return { message: String(error), stack: '' }; }
}

/** Builds the report that would be sent, or null when it shouldn't be. Exported for tests. */
export function buildReport(kind: ErrorKind, error: unknown, route: string) {
  const { message: raw, stack: rawStack } = describe(error);
  if (!raw || IGNORE.some((re) => re.test(raw))) return null;
  const message = scrub(raw).slice(0, 300);
  const stack = scrub(rawStack).slice(0, 4000);
  return {
    kind,
    message,
    stack: stack || null,
    route: route.slice(0, 120),
    platform: platform(),
    app_version: config.appVersion.slice(0, 32),
    fingerprint: fingerprint(kind, message, stack),
  };
}

export function reportError(kind: ErrorKind, error: unknown): void {
  try {
    if (!hasBackend || !useStore.getState().crashReports) return;
    // Path only: the query string and hash can carry search terms or sign-in tokens.
    const report = buildReport(kind, error, window.location.pathname);
    if (!report || sent.has(report.fingerprint) || sent.size >= MAX_PER_SESSION) return;
    sent.add(report.fingerprint);
    void send(report);
  } catch { /* reporting must never cause an error of its own */ }
}

async function send(report: NonNullable<ReturnType<typeof buildReport>>): Promise<void> {
  try {
    const sb = await getSupabase();
    // Use the session the device already has; never create a user just to report an error.
    const { data } = await sb.auth.getSession();
    if (!data.session) return;
    await sb.from('snapcook_client_errors').insert(report);
  } catch { /* dropped */ }
}

/** Uncaught errors and unhandled promise rejections, app-wide. Render crashes come from the ErrorBoundary. */
export function installErrorReporting(): void {
  window.addEventListener('error', (e) => reportError('error', e.error ?? e.message));
  window.addEventListener('unhandledrejection', (e) => reportError('rejection', e.reason));
}
